import {
  WATER_HEATING,
  type HeatingPhase,
  type HeatingSegment,
  type HeatingState,
  type StateFractions,
} from './particleModel';
import { type ExploreChallenge, type ModelField, firstModelError, formatModelNumber, modelFieldError } from './modelInputContract';

/**
 * Learner-controlled heating of a water sample at 101.325 kPa, from the same reviewed constants as
 * the fixed 1 g model (study-chemistry-tools-prd.md CT-DATA). Energy in = power × time; each stage
 * then uses Q = m × c × ΔT (warming) or Q = m × L (melting, boiling).
 */
export interface HeatingSetup {
  massG: number;
  startTemperatureC: number;
  powerW: number;
  timeS: number;
}

export type HeatingInput = keyof HeatingSetup;

export const HEATING_INPUT_LIMITS: Record<HeatingInput, { min: number; max: number; step: number; unit: string; label: string }> = {
  massG: { min: 1, max: 250, step: 1, unit: 'g', label: 'Mass of water' },
  startTemperatureC: { min: -30, max: 140, step: 1, unit: '°C', label: 'Starting temperature' },
  powerW: { min: 10, max: 3000, step: 10, unit: 'W', label: 'Heater power' },
  timeS: { min: 0, max: 7200, step: 1, unit: 's', label: 'Heating time' },
};

/** The same limits as a field list for the shared input panel. */
export const HEATING_FIELDS: ModelField<HeatingInput>[] = (Object.keys(HEATING_INPUT_LIMITS) as HeatingInput[]).map((key) => ({
  key,
  ...HEATING_INPUT_LIMITS[key],
}));

export const DEFAULT_HEATING_SETUP: HeatingSetup = { massG: 100, startTemperatureC: -20, powerW: 500, timeS: 5 };

/** The model stops here; more energy is reported as beyond the model, not extrapolated. */
export const HEATING_MODEL_MAX_C = 150;
/** The 3D beaker is drawn as a 250 mL beaker; the water level follows the mass up to this. */
export const BEAKER_CAPACITY_G = 250;

const W = WATER_HEATING;

export function heatingInputError(input: HeatingInput, value: number): string | null {
  return modelFieldError(HEATING_INPUT_LIMITS[input], value);
}

function heatCapacity(phase: HeatingPhase): number {
  if (phase === 'solid') return W.iceHeatCapacityJPerGK;
  if (phase === 'liquid') return W.waterHeatCapacityJPerGK;
  return W.steamHeatCapacityJPerGK;
}

/** Heating stages from the starting state to the model maximum. 0 °C starts as ice; 100 °C starts as liquid. */
export function heatingPlan(massG: number, startTemperatureC: number): HeatingSegment[] {
  const stages: Array<[HeatingPhase, number, number]> = [];
  if (startTemperatureC <= W.meltingPointC) {
    stages.push(['solid', startTemperatureC, W.meltingPointC], ['melting', W.meltingPointC, W.meltingPointC]);
  }
  if (startTemperatureC <= W.boilingPointC) {
    stages.push(['liquid', Math.max(startTemperatureC, W.meltingPointC), W.boilingPointC], ['boiling', W.boilingPointC, W.boilingPointC]);
  }
  stages.push(['gas', Math.max(startTemperatureC, W.boilingPointC), HEATING_MODEL_MAX_C]);

  let start = 0;
  return stages.map(([phase, from, to]) => {
    const energy = phase === 'melting'
      ? massG * W.fusionEnergyJPerG
      : phase === 'boiling'
        ? massG * W.vaporisationEnergyJPerG
        : massG * heatCapacity(phase) * (to - from);
    const segment = { phase, startEnergyJ: start, endEnergyJ: start + energy, startTemperatureC: from, endTemperatureC: to };
    start += energy;
    return segment;
  });
}

function fractionsFor(phase: HeatingPhase, progress: number): StateFractions {
  if (phase === 'solid') return { solid: 1, liquid: 0, gas: 0 };
  if (phase === 'melting') return { solid: 1 - progress, liquid: progress, gas: 0 };
  if (phase === 'liquid') return { solid: 0, liquid: 1, gas: 0 };
  if (phase === 'boiling') return { solid: 0, liquid: 1 - progress, gas: progress };
  return { solid: 0, liquid: 0, gas: 1 };
}

export interface HeatingStep {
  phase: HeatingPhase;
  title: string;
  formula: string;
  /** The formula with the learner's numbers, for the energy this stage actually received. */
  working: string;
  requiredJ: number;
  usedJ: number;
  status: 'done' | 'current' | 'not-reached';
}

export interface HeatingResult {
  energyJ: number;
  energyWorking: string;
  segments: HeatingSegment[];
  totalModelJ: number;
  state: HeatingState;
  steps: HeatingStep[];
  /** True when the energy goes past the model maximum; the state is shown at the maximum. */
  beyondModel: boolean;
}

const STEP_TITLES: Record<HeatingPhase, string> = {
  solid: 'Warm the ice',
  melting: 'Melt the ice',
  liquid: 'Warm the water',
  boiling: 'Boil the water',
  gas: 'Heat the steam',
};

export const formatQuantity = formatModelNumber;

function stepWorking(segment: HeatingSegment, massG: number, usedJ: number, complete: boolean): { formula: string; working: string } {
  const m = formatQuantity(massG);
  if (segment.phase === 'melting' || segment.phase === 'boiling') {
    const latent = segment.phase === 'melting' ? W.fusionEnergyJPerG : W.vaporisationEnergyJPerG;
    const verb = segment.phase === 'melting' ? 'melted' : 'boiled away';
    if (complete) return { formula: 'Q = m × L', working: `Q = ${m} g × ${latent} J/g = ${formatQuantity(segment.endEnergyJ - segment.startEnergyJ, 0)} J` };
    return {
      formula: 'mass changed = Q ÷ L',
      working: `${formatQuantity(usedJ, 0)} J ÷ ${latent} J/g = ${formatQuantity(usedJ / latent)} g ${verb} (of ${m} g)`,
    };
  }
  const c = heatCapacity(segment.phase);
  if (complete) {
    const dT = segment.endTemperatureC - segment.startTemperatureC;
    return { formula: 'Q = m × c × ΔT', working: `Q = ${m} g × ${c} J/(g·°C) × ${formatQuantity(dT)} °C = ${formatQuantity(segment.endEnergyJ - segment.startEnergyJ, 0)} J` };
  }
  const dT = usedJ / (massG * c);
  return {
    formula: 'ΔT = Q ÷ (m × c)',
    working: `${formatQuantity(usedJ, 0)} J ÷ (${m} g × ${c} J/(g·°C)) = ${formatQuantity(dT)} °C → ${formatQuantity(segment.startTemperatureC + dT)} °C`,
  };
}

/** Final state and the worked calculation for one learner setup. Throws on invalid input (fail closed). */
export function calculateHeating(setup: HeatingSetup): HeatingResult {
  const error = firstModelError(HEATING_FIELDS, setup);
  if (error) throw new Error(error);
  const energyJ = setup.powerW * setup.timeS;
  const segments = heatingPlan(setup.massG, setup.startTemperatureC);
  const totalModelJ = segments[segments.length - 1].endEnergyJ;
  const beyondModel = energyJ > totalModelJ;
  const modelEnergy = Math.min(energyJ, totalModelJ);

  const current = segments.find((segment) => modelEnergy < segment.endEnergyJ) ?? segments[segments.length - 1];
  const width = current.endEnergyJ - current.startEnergyJ;
  const progress = width === 0 ? 1 : (modelEnergy - current.startEnergyJ) / width;
  const temperatureC = current.startTemperatureC + (current.endTemperatureC - current.startTemperatureC) * progress;
  const state: HeatingState = {
    energyJ: modelEnergy,
    temperatureC,
    phase: current.phase,
    fractions: fractionsFor(current.phase, progress),
    plateau: current.phase === 'melting' || current.phase === 'boiling',
  };

  const steps = segments
    .filter((segment) => segment.endEnergyJ > segment.startEnergyJ)
    .map((segment) => {
      const requiredJ = segment.endEnergyJ - segment.startEnergyJ;
      const usedJ = clampNumber(modelEnergy - segment.startEnergyJ, 0, requiredJ);
      const status: HeatingStep['status'] = usedJ >= requiredJ ? 'done' : usedJ > 0 || segment === current ? 'current' : 'not-reached';
      const { formula, working } = stepWorking(segment, setup.massG, usedJ, status === 'done');
      return { phase: segment.phase, title: STEP_TITLES[segment.phase], formula, working, requiredJ, usedJ, status };
    });

  return {
    energyJ,
    energyWorking: `Q = P × t = ${formatQuantity(setup.powerW)} W × ${formatQuantity(setup.timeS)} s = ${formatQuantity(energyJ, 0)} J`,
    segments,
    totalModelJ,
    state,
    steps,
    beyondModel,
  };
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Heating time (whole seconds) that brings the sample to a point inside one stage, for presets. */
export function timeToReach(setup: HeatingSetup, phase: HeatingPhase, fraction: number): number | null {
  const segment = heatingPlan(setup.massG, setup.startTemperatureC).find((candidate) => candidate.phase === phase);
  if (!segment) return null;
  const energy = segment.startEnergyJ + (segment.endEnergyJ - segment.startEnergyJ) * fraction;
  const time = Math.round(energy / setup.powerW);
  return time <= HEATING_INPUT_LIMITS.timeS.max ? time : null;
}

/** What a heating challenge checks: the learner's setup and the model result for it. */
export interface HeatingChallengeInput {
  setup: HeatingSetup;
  result: HeatingResult;
}

export const HEATING_QUESTION = 'When you keep heating water, where does the energy go?';

/** Teaching tasks (CT-INTERACT C7). Each check reads only the model result, never the UI. */
export const HEATING_CHALLENGES: ExploreChallenge<HeatingChallengeInput>[] = [
  {
    id: 'flat-melting',
    prompt: 'Keep heating, but make the temperature stay at 0 °C.',
    hint: 'Start with ice and add just enough time to start melting.',
    isMet: ({ result }) => result.state.phase === 'melting' && result.energyJ > 0,
    why: 'While ice melts, the energy separates the particles instead of speeding them up, so the temperature stays at 0 °C until all the ice is gone.',
  },
  {
    id: 'half-melted',
    prompt: 'Melt exactly half of the ice (between 45 % and 55 % liquid).',
    hint: 'Use Q = m × L: half the mass needs half of 334 J for each gram.',
    isMet: ({ result }) =>
      result.state.phase === 'melting' && result.state.fractions.liquid >= 0.45 && result.state.fractions.liquid <= 0.55,
    why: 'Melting half the ice needs half of m × 334 J. The temperature is still 0 °C, because the energy is changing the state, not the temperature.',
  },
  {
    id: 'boil-away',
    prompt: 'Boil all of the water away, then compare the boiling step with the melting step.',
    hint: 'Raise the power or the time until the boiling step shows done.',
    isMet: ({ result }) => result.steps.some((step) => step.phase === 'boiling' && step.status === 'done'),
    why: 'Boiling needs 2260 J per gram but melting only 334 J per gram — about 7 times more — because particles in a gas must be pulled completely apart.',
  },
];
